import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SideBarDef,
  themeBalham,
  ToolPanelDef,
} from 'ag-grid-community';
import { AgGridColumnsFilterPanelComponent } from '../ag-grid-columns-filter-panel/ag-grid-columns-filter-panel.component';
import { ButtonComponent } from '../generic-button/button.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { columnsToolPanel, sideBarDef } from '../utils/ag-grid-col-defs';

@Component({
  selector: 'lib-common-ag-grid',
  standalone: true,
  imports: [
    CommonModule,
    AgGridAngular,
    MatIconModule,
    ButtonComponent,
    TranslateModule,
    MatTooltipModule,
    AgGridColumnsFilterPanelComponent,
    IconButtonComponent,
  ],
  templateUrl: './common-ag-grid.component.html',
  styleUrls: ['./common-ag-grid.component.scss'],
})
export class CommonAgGridComponent<T> implements AfterViewInit, OnChanges {
  // ---------------------------------------------------------------------------
  // Dependency Injection & View Queries
  // ---------------------------------------------------------------------------
  private readonly el = inject(ElementRef);

  public agGrid = viewChild.required<AgGridAngular>('agGrid');
  public overlay = viewChild.required(AgGridColumnsFilterPanelComponent);
  public columnsBtn = viewChild<ElementRef<HTMLElement>>('columnsBtn');

  // ---------------------------------------------------------------------------
  // Signal-based Inputs
  // ---------------------------------------------------------------------------
  public rowData = input<T[]>([]);
  public colDefs = input<ColDef<T>[]>([]);
  public defaultColDef = input<ColDef<T>>({
    suppressHeaderMenuButton: true,
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
  });
  public gridOptions = input<GridOptions<T>>({});
  public pinnedTopRowData = input<T[] | undefined>(undefined);
  public savedColumnState = input<ColumnState[] | null>(null);

  // Table State & Popups
  public isLoadingTable = input(false);
  public popupParent = input<HTMLElement | undefined>(undefined);
  public useBodyAsPopupParent = input(false);
  public hasError = input(false);
  public hasWarning = input(false);
  public warningMessage = input<string | undefined>(undefined);

  // Buttons, Icons & Action Configuration
  public showCreateButton = input(false);
  public buttonLabel = input<string | undefined>(undefined);
  public color = input<string | undefined>(undefined);
  public createButtonIcon = input<string | undefined>(undefined);
  public createEnabled = input(true);

  public showDeleteIcon = input(false);
  public deleteEnabled = input(true);

  public showDownloadIcon = input(false);
  public downloadEnabled = input(true);
  public exportFileName = input('export');
  public exportAllColumns = input(false);

  public showUploadIcon = input(false);
  public showColumnSettingsIcon = input(true);
  // ---------------------------------------------------------------------------
  // Signal-based Outputs
  // ---------------------------------------------------------------------------
  public createButtonClick = output<void>();
  public cellValueChanged = output<any>();
  public deleteSelectedRows = output<T[]>();
  public columnStateChanged = output<ColumnState[]>();

  // ---------------------------------------------------------------------------
  // Component State & Signals
  // ---------------------------------------------------------------------------
  public theme = themeBalham.withParams({ accentColor: '#006E8E' });
  public sideBar: SideBarDef = sideBarDef;
  public columnsToolPanel: ToolPanelDef = columnsToolPanel;

  // Reactive state signals
  public isRowSelected = signal(false);
  public isScrolledToRight = signal(false);

  public currentColumnState = signal<ColumnState[]>([]);
  private readonly initialColumnStateSignal = signal<ColumnState[]>([]);

  // Computed signals
  private readonly activeBaseline = computed(() => {
    const saved = this.savedColumnState();
    return saved && saved.length > 0 ? saved : this.initialColumnStateSignal();
  });

  public hasHiddenColumns = computed(() =>
    this.hasHiddenColumnsDiff(this.currentColumnState(), this.activeBaseline())
  );

  public hasHiddenColumnsFromOriginal = computed(() =>
    this.hasHiddenColumnsDiff(
      this.currentColumnState(),
      this.initialColumnStateSignal()
    )
  );

  public hasColumnOrderChanged = computed(() =>
    this.hasColumnOrderDiff(this.currentColumnState(), this.activeBaseline())
  );

  public hasColumnOrderChangedFromOriginal = computed(() =>
    this.hasColumnOrderDiff(
      this.currentColumnState(),
      this.initialColumnStateSignal()
    )
  );

  public hasSortChanged = computed(() =>
    this.hasSortDiff(this.currentColumnState(), this.activeBaseline())
  );

  public hasSortChangedFromOriginal = computed(() =>
    this.hasSortDiff(this.currentColumnState(), this.initialColumnStateSignal())
  );

  public canResetGrid = computed(
    () =>
      this.hasHiddenColumnsFromOriginal() ||
      this.hasColumnOrderChangedFromOriginal() ||
      this.hasSortChangedFromOriginal()
  );

  public isGridDirty = computed(
    () =>
      this.hasHiddenColumns() ||
      this.hasColumnOrderChanged() ||
      this.hasSortChanged()
  );

  public isPopupOpen = computed(() => this.overlay()?.showPopup() ?? false);

  public settingsIconSrc = computed(() => {
    if (this.isGridDirty()) {
      return 'images/settings-dirty.svg';
    }
    return this.isPopupOpen()
      ? 'images/settings-open.svg'
      : 'images/settings.svg';
  });

  onGridReady(params: GridReadyEvent) {
    if (this.rowData()?.length) {
      this.applyRowData(this.rowData());
    }

    const defaultState = params.api.getColumnState();
    this.initialColumnStateSignal.set(defaultState);

    const savedState = this.savedColumnState();
    if (savedState && savedState.length > 0) {
      params.api.applyColumnState({
        state: savedState,
        applyOrder: true,
      });
    }

    this.currentColumnState.set(params.api.getColumnState());

    params.api.addEventListener('columnVisible', () => this.onColumnEvent());
    params.api.addEventListener('columnMoved', () => this.onColumnEvent());
    params.api.addEventListener('sortChanged', () => this.onColumnEvent());
    params.api.addEventListener('bodyScroll', () => this.updateScrollState());
    params.api.addEventListener('gridSizeChanged', () =>
      this.updateScrollState()
    );
    params.api.addEventListener('columnResized', () =>
      this.updateScrollState()
    );
  }

  private onColumnEvent() {
    const api = this.api;
    if (api) {
      this.currentColumnState.set(api.getColumnState());
    }
  }

  private updateScrollState(): void {
    const vp = this.el.nativeElement.querySelector(
      '.ag-center-cols-viewport'
    ) as HTMLElement | null;
    if (!vp) return;
    const atRight = vp.scrollWidth - vp.scrollLeft - vp.clientWidth < 1;
    this.isScrolledToRight.set(atRight);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowData']) {
      this.applyRowData(changes['rowData'].currentValue ?? []);
    }

    if (
      changes['savedColumnState'] &&
      !changes['savedColumnState'].isFirstChange()
    ) {
      const savedState = this.savedColumnState();
      const api = this.api;
      if (savedState && api) {
        api.applyColumnState({
          state: savedState,
          applyOrder: true,
        });
        this.onColumnEvent();
      }
    }
  }

  ngAfterViewInit() {
    this.columnsToolPanel.parent =
      this.overlay().popupContentRef().nativeElement;
    this.sideBar.toolPanels = [this.columnsToolPanel];
    this.sideBar = { ...this.sideBar };
  }

  private applyRowData(newData: T[]): void {
    const api = this.api;
    if (!api) {
      // Grid not ready yet — onGridReady will apply rowData once it fires
      return;
    }

    // Collect any unsaved new rows currently in the grid before replacing data
    const unsavedNewRows: T[] = [];
    api.forEachNode((node) => {
      if (node.data && (node.data as any).isNew) {
        unsavedNewRows.push({ ...node.data });
      }
    });

    // Replace the grid's row data with the updated store data
    api.setGridOption('rowData', newData);

    // Re-add the unsaved new rows that were lost during the replacement
    if (unsavedNewRows.length > 0) {
      api.applyTransaction({ add: unsavedNewRows, addIndex: 0 });
    }
  }

  public get api(): GridApi<T> | undefined {
    return this.agGrid()?.api;
  }

  public get getColumnApi(): GridApi<T> | undefined {
    return this.api;
  }

  public onSelectionChanged(): void {
    this.isRowSelected.set((this.api?.getSelectedRows()?.length ?? 0) > 0);
  }

  public onDeleteIconClick(): void {
    if (this.isRowSelected()) {
      this.deleteSelectedRows.emit(this.api?.getSelectedRows() ?? []);
    }
  }

  public onCreateButtonClicked(): void {
    if (this.createEnabled()) {
      this.createButtonClick.emit();
    }
  }

  onExport() {
    const api = this.api;
    if (!api) return;

    const columnsToExport = this.exportAllColumns()
      ? api.getColumns()
      : api.getAllDisplayedColumns();
    const columns = columnsToExport
      ?.filter((col) => col.getColDef().headerName !== '')
      .map((col) => col.getColId());

    api.exportDataAsCsv({
      onlySelected: true,
      fileName: this.exportFileName(),
      columnKeys: columns,
    });
  }

  private hasHiddenColumnsDiff(
    current: ColumnState[],
    baseline: ColumnState[]
  ): boolean {
    if (current.length === 0 || baseline.length === 0) return false;
    const baselineMap = new Map(baseline.map((c) => [c.colId, !!c.hide]));
    return current.some(
      (c) => !!c.hide !== (baselineMap.get(c.colId) ?? false)
    );
  }

  private hasColumnOrderDiff(
    current: ColumnState[],
    baseline: ColumnState[]
  ): boolean {
    if (current.length === 0 || baseline.length === 0) return false;
    const currentOrder = current.map((c) => c.colId).join('|');
    const baselineOrder = baseline.map((c) => c.colId).join('|');
    return currentOrder !== baselineOrder;
  }

  private hasSortDiff(
    current: ColumnState[],
    baseline: ColumnState[]
  ): boolean {
    if (current.length === 0 || baseline.length === 0) return false;
    const baselineMap = new Map(
      baseline.map((c) => [
        c.colId,
        { sort: c.sort ?? null, sortIndex: c.sortIndex ?? null },
      ])
    );
    return current.some((c) => {
      const currentSort = c.sort ?? null;
      const currentSortIndex = c.sortIndex ?? null;
      const base = baselineMap.get(c.colId);
      const baseSort = base?.sort ?? null;
      const baseSortIndex = base?.sortIndex ?? null;
      return currentSort !== baseSort || currentSortIndex !== baseSortIndex;
    });
  }

  resetAllColumns() {
    const api = this.api;
    if (!api) return;

    const colIds = api.getColumns()?.map((col) => col.getColId()) ?? [];
    api.setColumnsVisible(colIds, true);
    api.applyColumnState({
      state: this.initialColumnStateSignal(),
      applyOrder: true,
    });
    this.currentColumnState.set(api.getColumnState());
  }

  onSaveColumns() {
    const api = this.api;
    if (!api) return;

    const stateToSave: ColumnState[] = api.getColumnState().map((col) => ({
      colId: col.colId,
      hide: col.hide,
      sort: col.sort,
      sortIndex: col.sortIndex,
      width: col.width,
    }));

    this.columnStateChanged.emit(stateToSave);
  }

  openPopup() {
    const api = this.api;
    if (!api) return;

    this.overlay().openPopup();
    api.closeToolPanel();
    const parent = this.overlay().popupContentRef().nativeElement;
    this.columnsToolPanel.parent = parent;
    this.sideBar = { ...this.sideBar, toolPanels: [this.columnsToolPanel] };

    setTimeout(() => {
      api.openToolPanel(this.columnsToolPanel.id);
    }, 0);

    this.overlay().addStyles(this.overlay().popupRef()?.nativeElement);
  }

  closePopup() {
    this.overlay().closePopup();
    this.api?.closeToolPanel();
  }

  public getPopupParent(): HTMLElement | undefined {
    if (this.popupParent()) {
      return this.popupParent();
    }
    if (this.useBodyAsPopupParent()) {
      return document.body;
    }
    return undefined;
  }
}
